/**
 * Password Reset Page
 *
 * Two-step flow for password reset:
 * 1. Request reset code (sent to email)
 * 2. Confirm reset with code and new password
 */

import { useState } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Anchor,
  Stepper,
  PinInput,
  Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../shared/components';
import { useAuth } from './contexts/AuthContext';
import { validatePassword } from '../../utils/authErrors';
import type { ResetPasswordFormValues, ConfirmResetPasswordFormValues } from '../../types/auth';
import { useSEO } from '../../shared/hooks';

export function ResetPassword() {
  useSEO({ title: 'Resetowanie Hasła', noindex: true });
  const navigate = useNavigate();
  const { resetPassword, confirmResetPassword, loading } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [userEmail, setUserEmail] = useState('');

  // Step 1: Request reset code
  const requestForm = useForm<ResetPasswordFormValues>({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Nieprawidłowy adres email'),
    },
  });

  // Step 2: Confirm reset with code and new password
  const confirmForm = useForm<ConfirmResetPasswordFormValues>({
    initialValues: {
      code: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      code: (value) => (value.length === 6 ? null : 'Kod musi składać się z 6 cyfr'),
      newPassword: (value) => {
        const validation = validatePassword(value);
        return validation.valid ? null : validation.message;
      },
      confirmPassword: (value, values) =>
        value !== values.newPassword ? 'Hasła nie są identyczne' : null,
    },
  });

  const handleRequestReset = async (values: ResetPasswordFormValues) => {
    try {
      await resetPassword(values.email);

      // Store email for confirmation step
      setUserEmail(values.email);

      // Show success notification
      notifications.show({
        title: 'Kod wysłany!',
        message: 'Sprawdź swoją skrzynkę email',
        color: 'green',
      });

      // Move to confirmation step
      setActiveStep(1);
    } catch (error) {
      notifications.show({
        title: 'Błąd',
        message: error instanceof Error ? error.message : 'Nie udało się wysłać kodu',
        color: 'red',
      });
    }
  };

  const handleConfirmReset = async (values: ConfirmResetPasswordFormValues) => {
    try {
      await confirmResetPassword(userEmail, values.code, values.newPassword);

      // Show success notification
      notifications.show({
        title: 'Hasło zmienione!',
        message: 'Możesz się teraz zalogować nowym hasłem',
        color: 'green',
      });

      // Navigate to login (navigate returns a promise but we intentionally don't await it)
      void navigate('/login');
    } catch (error) {
      notifications.show({
        title: 'Błąd',
        message: error instanceof Error ? error.message : 'Nie udało się zmienić hasła',
        color: 'red',
      });
    }
  };

  return (
    <Layout isAuthenticated={false}>
      <Container size="xs" py={80}>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Title order={2} ta="center" mb="md">
            Resetuj hasło
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb="xl">
            Odzyskaj dostęp do swojego konta
          </Text>

          <Stepper active={activeStep} onStepClick={setActiveStep} mb="xl">
            <Stepper.Step label="Krok 1" description="Podaj email">
              <form onSubmit={requestForm.onSubmit(handleRequestReset)}>
                <Stack gap="md" mt="xl">
                  <TextInput
                    label="Adres email"
                    placeholder="twoj@email.pl"
                    description="Wyślemy kod resetowania na ten adres"
                    required
                    {...requestForm.getInputProps('email')}
                  />

                  <Button type="submit" fullWidth loading={loading}>
                    Wyślij kod
                  </Button>
                </Stack>
              </form>
            </Stepper.Step>

            <Stepper.Step label="Krok 2" description="Ustaw nowe hasło">
              <form onSubmit={confirmForm.onSubmit(handleConfirmReset)}>
                <Stack gap="md" mt="xl">
                  <Text size="sm" c="dimmed">
                    Kod weryfikacyjny został wysłany na <strong>{userEmail}</strong>
                  </Text>

                  <div>
                    <Text size="sm" fw={500} mb="xs">
                      Kod weryfikacyjny
                    </Text>
                    <Group justify="center">
                      <PinInput
                        length={6}
                        type="number"
                        {...confirmForm.getInputProps('code')}
                      />
                    </Group>
                  </div>

                  <PasswordInput
                    label="Nowe hasło"
                    placeholder="Min. 8 znaków, wielkie i małe litery, cyfry, znaki specjalne"
                    description="Hasło musi zawierać co najmniej 8 znaków, w tym wielkie i małe litery, cyfry oraz znaki specjalne"
                    required
                    {...confirmForm.getInputProps('newPassword')}
                  />

                  <PasswordInput
                    label="Potwierdź nowe hasło"
                    placeholder="Powtórz nowe hasło"
                    required
                    {...confirmForm.getInputProps('confirmPassword')}
                  />

                  <Button type="submit" fullWidth loading={loading}>
                    Zmień hasło
                  </Button>

                  <Button
                    variant="subtle"
                    fullWidth
                    onClick={() => setActiveStep(0)}
                    disabled={loading}
                  >
                    Wróć do poprzedniego kroku
                  </Button>
                </Stack>
              </form>
            </Stepper.Step>
          </Stepper>

          <Text c="dimmed" size="sm" ta="center" mt="xl">
            Pamiętasz hasło?{' '}
            <Anchor component={Link} to="/login" fw={600}>
              Zaloguj się
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Layout>
  );
}
